import userApi from 'api/userApi';
import { useAppDispatch, useAppTranslation, useErrTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import WarningIcon from 'assets/icons/WarningIcon';
import { showConfirmModal, showErrorModal } from 'components/Modals/CommonModals';
import { onChangeAuthPage } from 'features/home/homeSlice';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import { GiveAwayResponseModel } from 'models/chatMessage';
import { UsePromotionResponseModel } from 'models/user';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import popper from '../../../../assets/images/popper.png';
import ReactConfetti from 'react-confetti';
interface SpinWheelPopupProps {
  isOpen?: boolean;
  onClose?: () => void;
  giveAwayData: GiveAwayResponseModel;
  onKYCRequired?: () => void;
}

const SpinWheelPopup: React.FC<SpinWheelPopupProps> = ({
  isOpen = false,
  onClose = () => {},
  giveAwayData,
  onKYCRequired = () => {},
}) => {
  const dispatch = useAppDispatch();
  const et = useErrTranslation();
  const t = useAppTranslation();
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState<UsePromotionResponseModel | null>(null);
  const [showResult, setShowResult] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { isUserLoggedIn, currentUser } = useUserDetail();

  const handleSpin = async (promotionCode: string) => {
    if (isSpinning) return;

    if (!isUserLoggedIn) {
      dispatch(onChangeAuthPage(0));
      return;
    }
    if (
      !currentUser?.kyc ||
      currentUser.kyc.status === 'INPROGRESS' ||
      currentUser.kyc.status === 'RETRY'
    ) {
      showConfirmModal({
        title: 'KYC Verification Required',
        content: <p>Please complete KYC to access this feature.</p>,
        width: 360,
        icon: <WarningIcon />,
        onOk: () => {
          onKYCRequired();
          navigate('/kyc');
        },
        okText: 'Verify KYC',
        cancelText: 'Skip',
        onCancel: () => {
          onKYCRequired();
        },
      });
      return;
    } else {
      setIsSpinning(true);
      setResult(null);
      setShowResult(false);

      try {
        const {
          ok,
          body: apiResult,
          error,
        } = await userApi.usePromotion({
          promotionCode: promotionCode,
        });

        if (error) {
          showErrorModal({
            content: et(error.message),
            onOk: () => {
              onClose();
            },
            title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
          });
          setIsSpinning(false);
          return;
        }
        if (ok && apiResult) {
          const spinItems = giveAwayData?.spinItems || [];
          const resultIndex = spinItems.findIndex(
            (item) =>
              item.amount === Number(apiResult.amount) &&
              item.walletReceive === apiResult.walletReceive,
          );

          if (resultIndex !== -1) {
            const segmentAngle = 360 / spinItems.length;
            const targetAngle = resultIndex * segmentAngle + segmentAngle / 2 - 0;

            const randomSpins = Math.floor(Math.random() * 3) + 5;
            const finalAngle = randomSpins * 360 + (360 - targetAngle);

            if (wheelRef.current) {
              wheelRef.current.style.transition = 'none';
              wheelRef.current.style.transform = 'rotate(0deg)';
              void wheelRef.current.offsetWidth;

              wheelRef.current.style.transition = 'transform 3s ease-out';
              wheelRef.current.style.transform = `rotate(${finalAngle}deg)`;
            }

            setTimeout(() => {
              setResult(apiResult);
              setShowResult(true);
              setIsSpinning(false);
            }, 3000);
          } else {
            setIsSpinning(false);
          }
        }
      } catch (err) {
        setIsSpinning(false);
      }
    }
  };

  const renderWheelSegments = () => {
    if (!giveAwayData?.spinItems) return null;

    const items = giveAwayData.spinItems;
    const segmentAngle = 360 / items.length;
    const radius = 140; // Bán kính wheel
    const centerX = 150;
    const centerY = 150;

    const colors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
    ];

    return (
      <svg width="100%" height="100%" viewBox="0 0 300 300" className="wheel-svg">
        {items.map((item, index) => {
          const startAngle = (index * segmentAngle - 0) * (Math.PI / 180);
          const endAngle = ((index + 1) * segmentAngle - 0) * (Math.PI / 180);

          const x1 = centerX + radius * Math.cos(startAngle);
          const y1 = centerY + radius * Math.sin(startAngle);
          const x2 = centerX + radius * Math.cos(endAngle);
          const y2 = centerY + radius * Math.sin(endAngle);

          const largeArcFlag = segmentAngle > 180 ? 1 : 0;

          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z',
          ].join(' ');

          const textAngle = (index * segmentAngle + segmentAngle / 2 - 0) * (Math.PI / 180);
          const textRadius = radius * 0.5;
          const textX = centerX + textRadius * Math.cos(textAngle);
          const textY = centerY + textRadius * Math.sin(textAngle);

          let textRotation = 0;
          const textAngleDegrees = index * segmentAngle + segmentAngle / 2;

          textRotation = textAngleDegrees ;

          return (
            <g key={index}>
              <path
                d={pathData}
                fill={colors[index % colors.length]}
                stroke="#fff"
                strokeWidth="2"
              />
              <text
                x={textX}
                y={textY}
                fill="white"
                fontSize="12"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                className="wheel-segment-text"
              >
                {item.amount} {giveAwayData.coinSymbol}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  const resetWheel = () => {
    setResult(null);
    setShowResult(false);
    if (wheelRef.current) {
      wheelRef.current.style.transform = 'rotate(0deg)';
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetWheel();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="spin-wheel-modal">
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('give-away.spin.title')}</h2>
          <button className="close-btn" onClick={onClose} disabled={isSpinning}>
            <CloseIcon width="20" height="20" />
          </button>
        </div>

        <div className="wheel-container">
          <div className="wheel-pointer"></div>
          <div ref={wheelRef} className={`wheel ${isSpinning ? 'spinning' : ''}`}>
            {renderWheelSegments()}
          </div>
        </div>

        <button
          className={`spin-btn ${isSpinning ? 'spinning' : ''}`}
          onClick={() => handleSpin(giveAwayData?.promotionCode)}
          disabled={isSpinning}
        >
          {isSpinning ? t('give-away.spin.spinning') : t('give-away.spin.spin')}
        </button>
      </div>
      {showResult && (
        <ReactConfetti
          gravity={0.3}
          numberOfPieces={200}
          colors={[
            '#FFD700',
            '#FF69B4',
            '#00BFFF',
            '#7B68EE',
            '#32CD32',
            '#FF4500',
            '#FF1493',
            '#00CED1',
          ]}
          recycle={false}
          style={{ zIndex: 99999 }}
          tweenDuration={1000}
        />
      )}
      {showResult && result && (
        <div className="result-modal">
          <div className="result-content">
            <img src={popper} width={100} height={100} />
            <h3>{t('give-away.spin.congratulations')}</h3>
            <div className="result-details">
              <p>
                {t('give-away.spin.won')}:{' '}
                <strong>
                  {result.amount} {giveAwayData?.coinSymbol}
                </strong>
              </p>
            </div>
            <button
              className="claim-btn"
              onClick={() => {
                setShowResult(false);
                onClose();
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheelPopup;
